import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// --- TYPE DEFINITIONS ---
type Point = { x: number; y: number };
type PathState = {
  points: Point[];
  controls: Point[];
};

// --- PROPS INTERFACE ---
interface MouthAnnotatorProps {
  imageUrl: string;
  onConfirm: (path: Point[]) => void;
  onCancel: () => void;
  initialPath: Point[] | null;
}

// --- INLINE ICON DEFINITIONS ---
const XIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const RefreshCwIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M3 2v6h6" /><path d="M21 12A9 9 0 0 0 6 5.3L3 8" /><path d="M21 22v-6h-6" /><path d="M3 12a9 9 0 0 0 15 6.7l3-2.7" /></svg>
);
const CheckCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>
);
const UndoIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" /></svg>
);
const ZoomInIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
);
const ZoomOutIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
);
const MoveIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" /><polyline points="15 19 12 22 9 19" /><polyline points="19 9 22 12 19 15" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="12" y1="2" x2="12" y2="22" /></svg>
);
const TrashIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
);

const MouthAnnotator: React.FC<MouthAnnotatorProps> = ({ imageUrl, onConfirm, onCancel, initialPath }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(new Image());
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // State
  const [pathState, setPathState] = useState<PathState>({ points: [], controls: [] });
  const [isClosed, setIsClosed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });
  const [draggingPoint, setDraggingPoint] = useState<{ type: 'anchor' | 'control', index: number } | null>(null);
  const [startPanPoint, setStartPanPoint] = useState({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<{ type: 'anchor' | 'control', index: number } | null>(null);
  const [showZoomHint, setShowZoomHint] = useState(true);
  const [pointerPos, setPointerPos] = useState<Point | null>(null);

  // Initialize with initialPath if available
  useEffect(() => {
    if (initialPath && initialPath.length > 0 && imageRef.current.naturalWidth > 0) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      const points = initialPath.map(p => ({
        x: p.x * naturalWidth,
        y: p.y * naturalHeight
      }));

      // Reconstruct controls (simple midpoint approximation if not stored)
      // In a real app, we might want to store controls too, but for now we infer them
      const controls: Point[] = [];
      for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];
        controls.push({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
      }

      setPathState({ points, controls });
      setIsClosed(true);
    }
  }, [initialPath]); // Run when initialPath changes (or image loads, handled separately)

  // Helper to check if image is loaded for initialization
  const handleImageLoad = useCallback(() => {
    if (initialPath && initialPath.length > 0) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      const points = initialPath.map(p => ({
        x: p.x * naturalWidth,
        y: p.y * naturalHeight
      }));
      const controls: Point[] = [];
      for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];
        controls.push({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
      }
      setPathState({ points, controls });
      setIsClosed(true);
    }
    resizeAndCenterView();
  }, [initialPath]);

  // Memoize draw function dependencies
  const drawParams = useMemo(() => ({
    points: pathState.points,
    controls: pathState.controls,
    isClosed,
    scale: viewState.scale,
    x: viewState.x,
    y: viewState.y,
    hoveredPoint,
    pointerPos,
    draggingPoint
  }), [pathState.points, pathState.controls, isClosed, viewState.scale, viewState.x, viewState.y, hoveredPoint, pointerPos, draggingPoint]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image.src || image.naturalWidth === 0) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    const { points, controls, isClosed, scale, x, y, hoveredPoint, pointerPos, draggingPoint } = drawParams;

    // Clear and Fill Background
    ctx.fillStyle = '#09090b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);

    // Draw image
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);

    // Draw Path
    if (points.length > 0) {
      // Main path glow
      ctx.strokeStyle = '#14b8a6';
      ctx.lineWidth = 3 / scale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = 'rgba(20, 184, 166, 0.6)';
      ctx.shadowBlur = 20 / scale;

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 0; i < points.length - 1; i++) {
        const control = controls[i];
        ctx.quadraticCurveTo(control.x, control.y, points[i + 1].x, points[i + 1].y);
      }
      if (isClosed) {
        ctx.quadraticCurveTo(controls[points.length - 1].x, controls[points.length - 1].y, points[0].x, points[0].y);
      }
      ctx.stroke();

      // Inner bright line
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#2dd4bf';
      ctx.lineWidth = 2 / scale;
      ctx.stroke();

      // Fill if closed
      if (isClosed) {
        const gradient = ctx.createLinearGradient(0, 0, image.naturalWidth, image.naturalHeight);
        gradient.addColorStop(0, 'rgba(20, 184, 166, 0.15)');
        gradient.addColorStop(1, 'rgba(45, 212, 191, 0.25)');
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      // Control Lines (Dashed)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1.5 / scale;
      ctx.setLineDash([5 / scale, 5 / scale]);
      for (let i = 0; i < points.length - 1; i++) {
        ctx.beginPath();
        ctx.moveTo(points[i].x, points[i].y);
        ctx.lineTo(controls[i].x, controls[i].y);
        ctx.lineTo(points[i + 1].x, points[i + 1].y);
        ctx.stroke();
      }
      if (isClosed) {
        const last = points.length - 1;
        ctx.beginPath();
        ctx.moveTo(points[last].x, points[last].y);
        ctx.lineTo(controls[last].x, controls[last].y);
        ctx.lineTo(points[0].x, points[0].y);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // Anchor Points
      points.forEach((p, i) => {
        const isStart = i === 0 && !isClosed;
        const isHovered = hoveredPoint?.type === 'anchor' && hoveredPoint.index === i;
        const isDragging = draggingPoint?.type === 'anchor' && draggingPoint.index === i;
        const radius = (isHovered || isDragging ? 9 : 7) / scale;

        if (isHovered || isDragging) {
          ctx.shadowColor = isStart ? 'rgba(245, 158, 11, 0.8)' : 'rgba(45, 212, 191, 0.8)';
          ctx.shadowBlur = 15 / scale;
        }

        ctx.fillStyle = isStart ? '#f59e0b' : '#14b8a6';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.5 / scale;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.stroke();
      });

      // Control Points
      controls.forEach((cp, i) => {
        const isHovered = hoveredPoint?.type === 'control' && hoveredPoint.index === i;
        const isDragging = draggingPoint?.type === 'control' && draggingPoint.index === i;
        const size = (isHovered || isDragging ? 12 : 10) / scale;

        if (isHovered || isDragging) {
          ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
          ctx.shadowBlur = 12 / scale;
        }

        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#14b8a6';
        ctx.lineWidth = 2 / scale;
        ctx.beginPath();
        ctx.roundRect(cp.x - size / 2, cp.y - size / 2, size, size, 2 / scale);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.stroke();
      });
    }

    ctx.restore();

    // --- MAGNIFIER ---
    if (draggingPoint && pointerPos) {
      const magSize = 120; // Diameter of magnifier
      const magScale = 2.5; // Zoom level
      const magX = pointerPos.x; // Screen coords
      const magY = pointerPos.y - 80; // Offset above cursor

      ctx.save();

      // Clip circle for magnifier
      ctx.beginPath();
      ctx.arc(magX, magY, magSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Fill background
      ctx.fillStyle = '#000';
      ctx.fillRect(magX - magSize / 2, magY - magSize / 2, magSize, magSize);

      // Draw magnified content
      // We need to draw the image and path again, but transformed
      // Center of magnifier looks at world coords of pointer
      const worldX = (pointerPos.x - x) / scale;
      const worldY = (pointerPos.y - y) / scale;

      // Translate so that worldX, worldY is at magX, magY
      ctx.translate(magX, magY);
      ctx.scale(scale * magScale, scale * magScale);
      ctx.translate(-worldX, -worldY);

      // Draw Image
      ctx.drawImage(image, 0, 0);

      // Draw Path (simplified for magnifier - just lines and points)
      if (points.length > 0) {
        ctx.strokeStyle = '#2dd4bf';
        ctx.lineWidth = 2 / (scale * magScale);
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 0; i < points.length - 1; i++) {
          const control = controls[i];
          ctx.quadraticCurveTo(control.x, control.y, points[i + 1].x, points[i + 1].y);
        }
        if (isClosed) {
          ctx.quadraticCurveTo(controls[points.length - 1].x, controls[points.length - 1].y, points[0].x, points[0].y);
        }
        ctx.stroke();

        // Points
        points.forEach((p) => {
          ctx.fillStyle = '#14b8a6';
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4 / (scale * magScale), 0, Math.PI * 2);
          ctx.fill();
        });
      }

      ctx.restore();

      // Draw Magnifier Border
      ctx.beginPath();
      ctx.arc(magX, magY, magSize / 2, 0, Math.PI * 2);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Crosshair in magnifier
      ctx.beginPath();
      ctx.moveTo(magX - 5, magY);
      ctx.lineTo(magX + 5, magY);
      ctx.moveTo(magX, magY - 5);
      ctx.lineTo(magX, magY + 5);
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

  }, [drawParams]);

  const resizeAndCenterView = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (canvas && image.naturalWidth > 0) {
      const { clientWidth: cW, clientHeight: cH } = canvas;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = cW * dpr;
      canvas.height = cH * dpr;
      canvas.style.width = `${cW}px`;
      canvas.style.height = `${cH}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);

      const { naturalWidth: iW, naturalHeight: iH } = image;
      const scale = Math.min(cW / iW, cH / iH) * 0.85;
      setViewState({ scale, x: (cW - iW * scale) / 2, y: (cH - iH * scale) / 2 });
    }
  }, []);

  useEffect(() => {
    const image = imageRef.current;
    image.crossOrigin = "Anonymous";
    image.src = imageUrl;
    image.onload = handleImageLoad;
    window.addEventListener('resize', resizeAndCenterView);
    return () => window.removeEventListener('resize', resizeAndCenterView);
  }, [imageUrl, handleImageLoad, resizeAndCenterView]);

  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [draw]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsSpacePressed(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Logic to delete selected point could go here if we had selection state
        // For now, undo serves as delete for the last point
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsSpacePressed(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pathState, isClosed]);

  useEffect(() => {
    if (showZoomHint) {
      const timer = setTimeout(() => setShowZoomHint(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showZoomHint]);

  const getWorldPos = useCallback((clientX: number, clientY: number): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left - viewState.x) / viewState.scale,
      y: (clientY - rect.top - viewState.y) / viewState.scale
    };
  }, [viewState]);

  // Helper to find closest point on segment
  const getClosestPointOnSegment = (p: Point, a: Point, b: Point) => {
    const atob = { x: b.x - a.x, y: b.y - a.y };
    const atop = { x: p.x - a.x, y: p.y - a.y };
    const len = atob.x * atob.x + atob.y * atob.y;
    let dot = atop.x * atob.x + atop.y * atob.y;
    const t = Math.min(1, Math.max(0, dot / len));
    return {
      x: a.x + atob.x * t,
      y: a.y + atob.y * t,
      t
    };
  };

  // --- POINTER EVENTS (Touch + Mouse) ---
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const worldPos = getWorldPos(e.clientX, e.clientY);
    setPointerPos({ x: e.clientX - canvasRef.current!.getBoundingClientRect().left, y: e.clientY - canvasRef.current!.getBoundingClientRect().top });

    if (isSpacePressed || e.button === 1) { // Middle click or Space+Click
      setIsPanning(true);
      setStartPanPoint({ x: e.clientX - viewState.x, y: e.clientY - viewState.y });
      return;
    }

    const hitRadius = 20 / viewState.scale; // Larger hit area for touch

    // Check Controls
    for (let i = pathState.controls.length - 1; i >= 0; i--) {
      if (Math.hypot(pathState.controls[i].x - worldPos.x, pathState.controls[i].y - worldPos.y) < hitRadius) {
        setDraggingPoint({ type: 'control', index: i });
        return;
      }
    }

    // Check Anchors
    for (let i = pathState.points.length - 1; i >= 0; i--) {
      if (Math.hypot(pathState.points[i].x - worldPos.x, pathState.points[i].y - worldPos.y) < hitRadius) {
        // Close shape logic
        if (i === 0 && pathState.points.length > 2 && !isClosed) {
          setIsClosed(true);
          setPathState(s => {
            const lastPoint = s.points[s.points.length - 1];
            const newControl = {
              x: (lastPoint.x + s.points[0].x) / 2,
              y: (lastPoint.y + s.points[0].y) / 2
            };
            return { ...s, controls: [...s.controls, newControl] };
          });
          return;
        }
        setDraggingPoint({ type: 'anchor', index: i });
        return;
      }
    }

    // Check if clicking on a line to insert point
    if (isClosed || pathState.points.length > 1) {
      for (let i = 0; i < pathState.points.length - (isClosed ? 0 : 1); i++) {
        const p1 = pathState.points[i];
        const p2 = pathState.points[(i + 1) % pathState.points.length];
        const closest = getClosestPointOnSegment(worldPos, p1, p2);
        const dist = Math.hypot(closest.x - worldPos.x, closest.y - worldPos.y);

        if (dist < hitRadius) {
          // Insert point
          setPathState(s => {
            const newPoints = [...s.points];
            const newControls = [...s.controls];

            // Insert new point at click position
            newPoints.splice(i + 1, 0, worldPos);

            // We need to add a control point.
            // For simplicity, we split the existing curve or line.
            // A better way is to keep the shape but that's complex math (De Casteljau split).
            // Simple approach: Add a control point between new point and next point.
            // And adjust the previous control point to be between prev point and new point.

            const prevP = newPoints[i];
            const nextP = newPoints[i + 2]; // The point that was at i+1

            // New control for the new segment (newPoint -> nextP)
            const newControl = {
              x: (worldPos.x + nextP.x) / 2,
              y: (worldPos.y + nextP.y) / 2
            };

            newControls.splice(i, 0, newControl); // This is actually wrong index logic for splice...

            // Let's simplify: Just rebuild controls for the affected segment
            // Actually, simpler: Just insert and let the user adjust.
            // But we need to maintain array lengths.
            // If we have N points, we have N-1 controls (open) or N controls (closed).

            // Re-calculate controls for the whole shape to be safe/smooth? 
            // No, that destroys user work.

            // Correct logic:
            // We are splitting segment i (p1 -> p2).
            // Old segment had control c1.
            // New segments: p1 -> newP, newP -> p2.
            // We need two controls now: c1_new (p1->newP) and c2_new (newP->p2).
            // We replace c1 with c1_new and insert c2_new.

            const oldControl = s.controls[i];
            const c1_new = { x: (p1.x + worldPos.x) / 2, y: (p1.y + worldPos.y) / 2 };
            const c2_new = { x: (worldPos.x + p2.x) / 2, y: (worldPos.y + p2.y) / 2 };

            newControls.splice(i, 1, c1_new, c2_new);

            return { points: newPoints, controls: newControls };
          });
          setDraggingPoint({ type: 'anchor', index: i + 1 }); // Start dragging the new point
          return;
        }
      }
    }

    if (isClosed) return;

    // Add new point (append)
    setPathState(s => {
      const newPoints = [...s.points, worldPos];
      let newControls = [...s.controls];
      if (newPoints.length > 1) {
        const p1 = newPoints[newPoints.length - 2];
        const p2 = newPoints[newPoints.length - 1];
        newControls.push({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
      }
      return { points: newPoints, controls: newControls };
    });
  }, [getWorldPos, isSpacePressed, viewState, pathState, isClosed]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    const worldPos = getWorldPos(e.clientX, e.clientY);
    const hitRadius = 20 / viewState.scale;

    // Check Anchors to delete
    for (let i = pathState.points.length - 1; i >= 0; i--) {
      if (Math.hypot(pathState.points[i].x - worldPos.x, pathState.points[i].y - worldPos.y) < hitRadius) {
        // Delete point
        setPathState(s => {
          if (s.points.length <= 3 && isClosed) {
            // Don't allow deleting if it breaks the polygon (min 3 points)
            return s;
          }

          const newPoints = [...s.points];
          const newControls = [...s.controls];

          newPoints.splice(i, 1);

          // Remove associated control.
          // If we remove point i, we merge segment (i-1 -> i) and (i -> i+1) into (i-1 -> i+1).
          // We need to remove one control.
          if (i < newControls.length) {
            newControls.splice(i, 1);
          } else {
            // Last point removed, remove last control
            newControls.pop();
          }

          // If we went below 3 points, open the shape
          if (isClosed && newPoints.length < 3) {
            setIsClosed(false);
          }

          return { points: newPoints, controls: newControls };
        });
        return;
      }
    }
  }, [getWorldPos, viewState, pathState, isClosed]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const worldPos = getWorldPos(e.clientX, e.clientY);
    setPointerPos({ x: e.clientX - canvasRef.current!.getBoundingClientRect().left, y: e.clientY - canvasRef.current!.getBoundingClientRect().top });
    const hitRadius = 20 / viewState.scale;

    if (isPanning) {
      setViewState(v => ({
        ...v,
        x: e.clientX - startPanPoint.x,
        y: e.clientY - startPanPoint.y
      }));
      return;
    }

    if (draggingPoint) {
      setPathState(s => {
        let newPoints = [...s.points];
        let newControls = [...s.controls];
        if (draggingPoint.type === 'anchor') {
          newPoints[draggingPoint.index] = worldPos;
          // Update adjacent controls to keep smooth? For now just move point.
          // Optional: Move adjacent controls to maintain relative position
        } else {
          newControls[draggingPoint.index] = worldPos;
        }
        return { points: newPoints, controls: newControls };
      });
      return;
    }

    // Update hover state
    let newHovered: { type: 'anchor' | 'control', index: number } | null = null;

    for (let i = 0; i < pathState.controls.length; i++) {
      if (Math.hypot(pathState.controls[i].x - worldPos.x, pathState.controls[i].y - worldPos.y) < hitRadius) {
        newHovered = { type: 'control', index: i };
        break;
      }
    }

    if (!newHovered) {
      for (let i = 0; i < pathState.points.length; i++) {
        if (Math.hypot(pathState.points[i].x - worldPos.x, pathState.points[i].y - worldPos.y) < hitRadius) {
          newHovered = { type: 'anchor', index: i };
          break;
        }
      }
    }

    if (JSON.stringify(newHovered) !== JSON.stringify(hoveredPoint)) {
      setHoveredPoint(newHovered);
    }
  }, [getWorldPos, isPanning, startPanPoint, draggingPoint, pathState, viewState.scale, hoveredPoint]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsPanning(false);
    setDraggingPoint(null);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;

    setViewState(v => {
      const newScale = Math.max(0.2, Math.min(v.scale * delta, 15));
      const worldX = (mouseX - v.x) / v.scale;
      const worldY = (mouseY - v.y) / v.scale;
      const newX = mouseX - worldX * newScale;
      const newY = mouseY - worldY * newScale;
      return { scale: newScale, x: newX, y: newY };
    });
  }, []);

  const handleReset = useCallback(() => {
    setPathState({ points: [], controls: [] });
    setIsClosed(false);
    setHoveredPoint(null);
  }, []);

  const handleUndo = useCallback(() => {
    if (isClosed) {
      setIsClosed(false);
      setPathState(s => ({ ...s, controls: s.controls.slice(0, -1) }));
    } else if (pathState.points.length > 0) {
      setPathState(s => ({
        points: s.points.slice(0, -1),
        controls: s.controls.slice(0, -1)
      }));
    }
  }, [isClosed, pathState.points.length]);

  const handleZoomIn = useCallback(() => {
    setViewState(v => {
      const canvas = canvasRef.current;
      if (!canvas) return v;
      const centerX = canvas.clientWidth / 2;
      const centerY = canvas.clientHeight / 2;
      const newScale = Math.min(v.scale * 1.3, 15);
      const worldX = (centerX - v.x) / v.scale;
      const worldY = (centerY - v.y) / v.scale;
      return {
        scale: newScale,
        x: centerX - worldX * newScale,
        y: centerY - worldY * newScale
      };
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setViewState(v => {
      const canvas = canvasRef.current;
      if (!canvas) return v;
      const centerX = canvas.clientWidth / 2;
      const centerY = canvas.clientHeight / 2;
      const newScale = Math.max(v.scale * 0.7, 0.2);
      const worldX = (centerX - v.x) / v.scale;
      const worldY = (centerY - v.y) / v.scale;
      return {
        scale: newScale,
        x: centerX - worldX * newScale,
        y: centerY - worldY * newScale
      };
    });
  }, []);

  const handleConfirm = useCallback(() => {
    const { points, controls } = pathState;
    if (points.length < 3 || !isClosed) return;

    const finalPath: Point[] = [];
    const steps = 30;

    for (let i = 0; i < points.length; i++) {
      const p0 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p1 = controls[i];

      for (let j = 0; j < steps; j++) {
        const t = j / steps;
        const mt = 1 - t;
        finalPath.push({
          x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
          y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y
        });
      }
    }

    const { naturalWidth, naturalHeight } = imageRef.current;
    onConfirm(finalPath.map(p => ({
      x: p.x / naturalWidth,
      y: p.y / naturalHeight
    })));
  }, [pathState, isClosed, onConfirm]);

  const getInstruction = () => {
    if (pathState.points.length === 0) {
      return "Click to place first point around the smile area";
    }
    if (!isClosed) {
      return "Continue placing points • Click orange point to complete";
    }
    return "Drag points for precision • Magnifier shows detail";
  };

  const zoomPercentage = Math.round(viewState.scale * 100);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex bg-zinc-950 text-white"
    >
      <main
        ref={containerRef}
        onWheel={handleWheel}
        className="flex-1 flex items-center justify-center relative select-none overflow-hidden touch-none"
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none"
          style={{
            cursor: isSpacePressed
              ? isPanning
                ? 'grabbing'
                : 'grab'
              : draggingPoint || hoveredPoint
                ? 'none' // Hide cursor when interacting to show magnifier clearly, or use custom cursor
                : 'crosshair'
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onDoubleClick={handleDoubleClick}
        />

        {/* Zoom Hint */}
        <AnimatePresence>
          {showZoomHint && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-6 left-6 z-20 px-4 py-2 text-xs font-medium rounded-lg bg-black/60 backdrop-blur-md border border-white/10 text-zinc-300 flex items-center gap-2 pointer-events-none"
            >
              <MoveIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Space + Drag: Pan • Scroll: Zoom</span>
              <span className="sm:hidden">Two-finger drag to pan</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Instruction Pill */}
        <motion.div
          key={getInstruction()}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="absolute top-6 left-1/2 -translate-x-1/2 z-20 px-6 py-3 text-sm font-medium rounded-xl bg-black/70 backdrop-blur-xl border border-white/10 shadow-2xl pointer-events-none"
        >
          <p className="text-zinc-100 text-center whitespace-nowrap">{getInstruction()}</p>
        </motion.div>

        {/* Floating Controls (Zoom) */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="absolute left-6 bottom-6 z-20 flex flex-col gap-2"
        >
          <motion.button
            onClick={handleZoomIn}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-12 h-12 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            title="Zoom In"
          >
            <ZoomInIcon className="w-5 h-5" />
          </motion.button>
          <motion.button
            onClick={handleZoomOut}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-12 h-12 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            title="Zoom Out"
          >
            <ZoomOutIcon className="w-5 h-5" />
          </motion.button>
          <div className="w-12 h-12 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center text-xs font-semibold text-teal-400">
            {zoomPercentage}%
          </div>
        </motion.div>
      </main>

      {/* Sidebar */}
      <aside className="w-80 flex-shrink-0 bg-zinc-950/90 backdrop-blur-2xl border-l border-white/10 flex flex-col shadow-2xl z-30">
        <motion.div
          className="flex-1 p-8 flex flex-col overflow-y-auto"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
        >
          <motion.div
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 w-fit"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
            <p className="text-xs font-bold tracking-widest text-teal-400">STEP 02</p>
          </motion.div>

          <motion.h2
            variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            className="text-4xl font-bold mt-4 text-white leading-tight font-serif"
          >
            Define Mouth Area
          </motion.h2>

          <motion.p
            variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            className="text-zinc-400 mt-4 text-sm leading-relaxed"
          >
            Create a precise selection around the lips and teeth. This tells the AI exactly where to work its magic.
          </motion.p>

          <div className="mt-10 space-y-6">
            <motion.div
              variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
              className="flex items-start gap-4 group"
            >
              <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex-shrink-0 flex items-center justify-center text-teal-400 font-bold text-sm group-hover:scale-110 transition-transform">
                1
              </div>
              <div>
                <h3 className="font-semibold text-zinc-100">Draw Path</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                  Tap around the lips to create an outline.
                </p>
              </div>
            </motion.div>

            <motion.div
              variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
              className="flex items-start gap-4 group"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex-shrink-0 flex items-center justify-center text-amber-400 font-bold text-sm group-hover:scale-110 transition-transform">
                2
              </div>
              <div>
                <h3 className="font-semibold text-zinc-100">Close Shape</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                  Tap the starting point (orange) to finish.
                </p>
              </div>
            </motion.div>

            <motion.div
              variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
              className="flex items-start gap-4 group"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex-shrink-0 flex items-center justify-center text-purple-400 font-bold text-sm group-hover:scale-110 transition-transform">
                3
              </div>
              <div>
                <h3 className="font-semibold text-zinc-100">Refine</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                  Drag points to adjust. Use the magnifier for detail.
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
            className="mt-auto pt-8"
          >
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-4 text-xs text-zinc-400">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-teal-400" />
                  <span>Anchor</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-sm bg-white border border-teal-400" />
                  <span>Control</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="p-6 border-t border-white/10 bg-black/20 space-y-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center gap-3">
            <motion.button
              onClick={handleUndo}
              disabled={pathState.points.length === 0}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 py-3 rounded-xl text-sm font-semibold bg-white/5 text-zinc-300 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 border border-white/5"
            >
              <UndoIcon className="w-4 h-4" />
              Undo
            </motion.button>
            <motion.button
              onClick={handleReset}
              disabled={pathState.points.length === 0}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 py-3 rounded-xl text-sm font-semibold bg-white/5 text-zinc-300 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 border border-white/5"
            >
              <TrashIcon className="w-4 h-4" />
              Clear
            </motion.button>
          </div>

          <motion.button
            onClick={handleConfirm}
            disabled={!isClosed}
            whileHover={{ scale: isClosed ? 1.02 : 1 }}
            whileTap={{ scale: isClosed ? 0.98 : 1 }}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-white font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-teal-500/20 relative overflow-hidden group"
            style={{
              background: isClosed
                ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'
                : 'linear-gradient(135deg, #3f3f46 0%, #27272a 100%)'
            }}
          >
            {isClosed && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              />
            )}
            <CheckCircleIcon className="w-5 h-5 relative z-10" />
            <span className="relative z-10">Confirm Selection</span>
          </motion.button>

          <motion.button
            onClick={onCancel}
            whileHover={{ scale: 1.01 }}
            className="w-full text-center py-2 rounded-lg text-sm font-medium text-zinc-500 hover:text-zinc-300 hover:bg-white/5 transition-all"
          >
            Cancel
          </motion.button>
        </motion.div>
      </aside>
    </motion.div>
  );
};

export default MouthAnnotator;